import { useMemo } from 'react';
import { useFinance } from '@/contexts/FinanceContext';
import type { FinanceAccount } from '@/types/finance';
import {
  buildAccountStatement,
  type AccountStatement,
  type StatementEntry,
} from './accountStatement';

export interface StatementViewRow {
  entry: StatementEntry;
  /** "Transferência" para transfers; senão nome da categoria ou "Múltiplas (N)". */
  categoryName: string;
  counterpartyName?: string;
  /** Nome da outra conta quando a linha é transferência. */
  counterpartAccountName?: string;
}

export interface AccountStatementViewData {
  account?: FinanceAccount;
  statement?: AccountStatement;
  rows: StatementViewRow[];
}

export function useAccountStatement(
  accountId: string | undefined,
  fromDate: Date,
  toDate: Date,
): AccountStatementViewData {
  const { transactions, accounts, categories, counterparties } = useFinance();

  return useMemo(() => {
    const account = accountId ? accounts.find((a) => a.id === accountId) : undefined;
    if (!account) return { rows: [] };

    const statement = buildAccountStatement({
      transactions,
      accountId: account.id,
      initialBalance: account.initialBalance,
      fromDate,
      toDate,
    });

    const rows: StatementViewRow[] = statement.rows.map((entry) => {
      const { tx } = entry;
      const categoryName = entry.isTransfer
        ? 'Transferência'
        : tx.categorySplits && tx.categorySplits.length > 0
          ? `Múltiplas (${tx.categorySplits.length})`
          : (categories.find((c) => c.id === tx.categoryId)?.name ?? '—');
      const counterpartyName = tx.counterpartyId
        ? counterparties.find((cp) => cp.id === tx.counterpartyId)?.name
        : undefined;
      const counterpartAccountName = entry.transferCounterpartAccountId
        ? accounts.find((a) => a.id === entry.transferCounterpartAccountId)?.name
        : undefined;
      return {
        entry,
        categoryName,
        ...(counterpartyName && { counterpartyName }),
        ...(counterpartAccountName && { counterpartAccountName }),
      };
    });

    return { account, statement, rows };
  }, [accountId, fromDate, toDate, transactions, accounts, categories, counterparties]);
}
